import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../store/hooks/useAppDispatch";
import { selectFieldsStateConfigurationHash } from "../../../store/selectors/fieldsStateSelectors";
import {
  selectVersionHistory,
  selectVersionHistoryBackupConfiguration,
  selectVersionHistoryCurrentConfiguration,
  selectVersionHistoryPreviewData,
} from "../../../store/selectors/versionHistorySelectors";
import { applyPreviewConfiguration, getCurrentConfigurations } from "../../../store/thunks/currentReportThunks";
import type { PreviewableVersion } from "../../../store/types/versionHistoryState.types";
import { createUiSettingsAction, createVersionHistoryAction } from "../../../store/utils/storeUtils";
import type { VersionId } from "./versionHistory.types";

const useVersionHistory = () => {
  const dispatch = useAppDispatch();
  const previewData = useSelector(selectVersionHistoryPreviewData);
  const backupConfiguration = useSelector(selectVersionHistoryBackupConfiguration);
  const currentConfiguration = useSelector(selectVersionHistoryCurrentConfiguration);
  const versions = useSelector(selectVersionHistory);

  const [selectedVersionId, setSelectedVersionId] = useState<VersionId | undefined>(previewData?.id);
  const [currentVersionId, setCurrentVersionId] = useState<VersionId | undefined>(undefined);
  const configurationHash = useSelector(selectFieldsStateConfigurationHash);

  const handleClosePage = () => {
    dispatch(createUiSettingsAction("setVersionHistoryOpen", false));
    dispatch(createVersionHistoryAction("updateBackupConfiguration", undefined));
    dispatch(createVersionHistoryAction("updateCurrentConfiguration", undefined));
  };

  const onPreviewVersion = async (version: PreviewableVersion) => {
    if (backupConfiguration === undefined) {
      dispatch(createVersionHistoryAction("updateBackupConfiguration", currentConfiguration));
    }
    setSelectedVersionId(version.id);
    dispatch(createVersionHistoryAction("updatePreviewConfiguration", version));

    await dispatch(
      applyPreviewConfiguration({
        datasetId: version.dataSetId,
        configuration: version.configuration,
        reportType: version.reportType,
      })
    ).unwrap();
  };

  useEffect(() => {
    if (previewData) {
      //In this case a preview configuration is applied we don't need it to be tracked
      return;
    }
    //A configuration before preview is applied.
    (async () => {
      const config = await dispatch(getCurrentConfigurations()).unwrap();
      dispatch(createVersionHistoryAction("updateCurrentConfiguration", config));
      if (config.isReportChanged) {
        setSelectedVersionId("unsaved");
        setCurrentVersionId("unsaved");
        return;
      }
      const activeVersion = versions.find((version) => !version.isDeleted);
      if (activeVersion) {
        setSelectedVersionId(activeVersion.id);
        setCurrentVersionId(activeVersion.id);
      }
    })();
  }, [dispatch, versions, previewData, configurationHash]);

  return {
    currentConfiguration,
    isReportChanged: currentConfiguration?.isReportChanged ?? false,
    selectedVersionId,
    previewData,
    currentVersionId,
    onPreviewVersion,
    handleClose: handleClosePage,
  };
};

export default useVersionHistory;
